import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * BuyingRhythm — which months this agency actually signs in.
 *
 * Nobody has this and every rep needs it. California's June year-end spike is
 * folklore until you can see whether it is true of THIS agency: a calendar of
 * month × fiscal year, cell darkness by spend, reads the rhythm in one glance
 * and answers "when do I need the quote in front of them".
 *
 * The x axis is the twelve months of the FISCAL year in fiscal order, so the
 * year-end column sits at the right-hand edge where a spike belongs. The data
 * arrives as calendar months (the endpoint buckets it that way on purpose —
 * bucketing by fiscal month server-side would hide the boundary effect by
 * construction); the fiscal mapping happens here, against whichever calendar
 * the caller is showing.
 *
 * Presentational: data via props, no fetching, colours from CSS variables.
 */
import React, { useMemo } from 'react';
const MONTH_ABBR = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const MONTH_NAME = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
const defaultFormatValue = (v) => {
    if (v >= 1000000000)
        return `$${(v / 1000000000).toFixed(1)}B`;
    if (v >= 1000000)
        return `$${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000)
        return `$${(v / 1000).toFixed(0)}K`;
    return `$${Math.round(v)}`;
};
/** Both registry calendars name a fiscal year by its ENDING calendar year. */
function fiscalYearOf(year, month1, startMonth) {
    return month1 >= startMonth ? year + 1 : year;
}
export const BuyingRhythm = ({ months, fiscalStartMonth = 7, years = 5, onPick, formatValue = defaultFormatValue, fill = false, }) => {
    const { rows, max, peakMonthIndex, peakShare } = useMemo(() => {
        const byKey = new Map();
        for (const m of months || []) {
            if (!m?.month)
                continue;
            const [y, mo] = m.month.split('-').map(Number);
            if (!Number.isFinite(y) || !Number.isFinite(mo))
                continue;
            const fy = fiscalYearOf(y, mo, fiscalStartMonth);
            const idx = (mo - fiscalStartMonth + 12) % 12;
            const key = `${fy}:${idx}`;
            const prev = byKey.get(key) || { spend: 0, poCount: 0 };
            byKey.set(key, {
                spend: prev.spend + (Number(m.spend) || 0),
                poCount: prev.poCount + (Number(m.poCount) || 0),
            });
        }
        if (byKey.size === 0) {
            return { rows: [], max: 0, peakMonthIndex: -1, peakShare: 0 };
        }
        const fys = [...new Set([...byKey.keys()].map((k) => Number(k.split(':')[0])))].sort();
        const shown = fys.slice(-years);
        const total = [...byKey.values()].reduce((s, v) => s + v.spend, 0);
        // Which fiscal month carries the most, across every year shown — the
        // headline the calendar exists to produce.
        const byMonthIdx = new Array(12).fill(0);
        let maxCell = 0;
        const built = shown.map((fy) => ({
            fiscalYear: fy,
            cells: Array.from({ length: 12 }, (_, idx) => {
                const v = byKey.get(`${fy}:${idx}`) || { spend: 0, poCount: 0 };
                byMonthIdx[idx] += v.spend;
                if (v.spend > maxCell)
                    maxCell = v.spend;
                const calendarMonth = ((fiscalStartMonth - 1 + idx) % 12) + 1;
                return {
                    fiscalYear: fy,
                    monthIndex: idx,
                    calendarMonth,
                    label: MONTH_NAME[calendarMonth - 1],
                    spend: v.spend,
                    poCount: v.poCount,
                    share: total > 0 ? v.spend / total : 0,
                };
            }),
        }));
        const peak = byMonthIdx.indexOf(Math.max(...byMonthIdx));
        const grand = byMonthIdx.reduce((a, b) => a + b, 0);
        return {
            rows: built,
            max: maxCell,
            peakMonthIndex: grand > 0 ? peak : -1,
            peakShare: grand > 0 ? byMonthIdx[peak] / grand : 0,
        };
    }, [months, fiscalStartMonth, years]);
    if (rows.length === 0) {
        return (_jsx("div", { className: `chs__empty${fill ? ' chs__empty--fill' : ''}`, children: "No dated spend recorded for this agency." }));
    }
    const headerMonths = Array.from({ length: 12 }, (_, idx) => {
        const cal = ((fiscalStartMonth - 1 + idx) % 12) + 1;
        return { idx, abbr: MONTH_ABBR[cal - 1], name: MONTH_NAME[cal - 1] };
    });
    return (_jsxs("div", { className: `brh${fill ? ' chfill' : ''}`, children: [_jsxs("div", { className: "brh-grid brh-grid--months", children: [_jsx("span", { className: "brh-corner", "aria-hidden": "true" }), headerMonths.map((h) => (_jsx("abbr", { className: `brh-head${h.idx === peakMonthIndex ? ' brh-head--peak' : ''}`, title: h.name, children: h.abbr }, h.idx))), rows.map((row) => (_jsxs(React.Fragment, { children: [_jsxs("span", { className: "brh-year", children: ["FY", String(row.fiscalYear).slice(-2)] }), row.cells.map((cell) => {
                                // Square-root, not linear: one enormous month otherwise flattens
                                // every other cell to the same near-white and the rhythm — the
                                // thing being read — disappears into the outlier.
                                const intensity = max > 0 ? Math.sqrt(cell.spend / max) : 0;
                                const empty = !(cell.spend > 0);
                                const title = empty
                                    ? `${cell.label} FY${row.fiscalYear} — nothing recorded`
                                    : `${cell.label} FY${row.fiscalYear} — ${formatValue(cell.spend)}`
                                        + (cell.poCount ? ` across ${cell.poCount} PO${cell.poCount === 1 ? '' : 's'}` : '');
                                const Tag = (onPick && !empty ? 'button' : 'span');
                                return (_jsx(Tag, { type: onPick && !empty ? 'button' : undefined, className: `brh-cell${empty ? ' brh-cell--empty' : ''}`, style: empty ? undefined : { '--i': intensity.toFixed(3) }, title: title, "aria-label": title, onClick: onPick && !empty ? () => onPick(cell) : undefined }, cell.monthIndex));
                            })] }, row.fiscalYear)))] }), peakMonthIndex >= 0 && (_jsxs("p", { className: "brh-read", children: [_jsx("strong", { children: MONTH_NAME[((fiscalStartMonth - 1 + peakMonthIndex) % 12)] }), ' ', "is ", Math.round(peakShare * 100), "% of the money.", peakShare > 0.15 && ' Land the quote a quarter before it.'] }))] }));
};
export default BuyingRhythm;
